import { Box, Paper } from "@mui/material";
import { SECONDS_IN_DAY } from "components/manualReview/utils/timeUtils";
import { Dayjs } from "dayjs";
import { useCallback, useRef } from "react";
import {
  HeatSummary,
  LocationExceptionalStatusRead,
  LocationsLoadingTimeline,
  ReviewPeriodData,
  ReviewTimelineTick,
  SegmentStatusListItem,
} from "src/store/api/generatedApi";
import { ExpandedLocationLane } from "./ExpandedLocationLane";
import { HourRailLabels, HourRails } from "./HourRails";
import { LocationsLoadingMarkers } from "./LocationsLoadingMarkers";
import {
  DAY_TIMELINE_HEIGHT,
  EXPANDED_LANE,
  HOUR_LABEL,
} from "./timelineLayout";
import { useTimelineScrub } from "./useTimelineScrub";
import { ViewingWindowIndicator } from "./ViewingWindowIndicator";
import { ViewingWindowTimeLabel } from "./ViewingWindowTimeLabel";

interface DayTimelineProps {
  viewingWindowStart: Dayjs;
  viewingWindowEnd: Dayjs;
  heats: HeatSummary[];
  ticks: ReviewTimelineTick[];
  reviewPeriods: ReviewPeriodData[];
  exceptionalStatuses?: LocationExceptionalStatusRead[];
  onWindowChange: (newStart: Dayjs) => void;
  dayStart: Dayjs;
  dayEnd: Dayjs;
  segmentCoverageItems?: readonly SegmentStatusListItem[];
  segmentLengthSec?: number;
  emphasizeSparseSegments?: boolean;
  locationsLoadingTimeline?: LocationsLoadingTimeline;
}

export const DayTimeline = ({
  viewingWindowStart,
  viewingWindowEnd,
  heats,
  ticks,
  reviewPeriods,
  exceptionalStatuses = [],
  onWindowChange,
  dayStart,
  dayEnd,
  segmentCoverageItems,
  segmentLengthSec,
  emphasizeSparseSegments,
  locationsLoadingTimeline,
}: DayTimelineProps) => {
  const plotAreaRef = useRef<HTMLDivElement>(null);

  const getPositionPercent = useCallback(
    (time: Dayjs): number =>
      (time.diff(dayStart, "second") / SECONDS_IN_DAY) * 100,
    [dayStart]
  );

  const {
    isScrubbing,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleMouseLeave,
  } = useTimelineScrub({ plotAreaRef, dayStart, dayEnd, onWindowChange });

  const windowLeftPercent = getPositionPercent(viewingWindowStart);
  const windowWidthPercent =
    getPositionPercent(viewingWindowEnd) - windowLeftPercent;

  return (
    <Box sx={{ mb: 2 }}>
      <Paper
        ref={plotAreaRef}
        sx={{
          height: DAY_TIMELINE_HEIGHT,
          position: "relative",
          cursor: isScrubbing ? "grabbing" : "grab",
          overflow: "hidden",
          userSelect: "none",
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
      >
        <HourRails />
        <HourRailLabels dayStart={dayStart} slot={HOUR_LABEL} />
        <ExpandedLocationLane
          heats={heats}
          ticks={ticks}
          reviewPeriods={reviewPeriods}
          exceptionalStatuses={exceptionalStatuses}
          segmentCoverageItems={segmentCoverageItems}
          segmentLengthSec={segmentLengthSec}
          emphasizeSparseSegments={emphasizeSparseSegments}
          dayStart={dayStart}
          getPositionPercent={getPositionPercent}
        />
        {locationsLoadingTimeline ? (
          <LocationsLoadingMarkers
            buckets={locationsLoadingTimeline.buckets}
            totalLocations={locationsLoadingTimeline.locations.length}
            slot={EXPANDED_LANE.loading}
            getPositionPercent={getPositionPercent}
          />
        ) : null}
        <ViewingWindowIndicator
          windowLeftPercent={windowLeftPercent}
          windowWidthPercent={windowWidthPercent}
        />
        <ViewingWindowTimeLabel
          windowLeftPercent={windowLeftPercent}
          viewingWindowStart={viewingWindowStart}
          viewingWindowEnd={viewingWindowEnd}
        />
      </Paper>
    </Box>
  );
};
